/**
 * CompanyDashboard Component
 * =========================
 * 
 * Dashboard for viewing and managing a specific company.
 * Shows secondary sidebar with company management options.
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../layout/AdminLayout';
import UserLayout from '../../../user-v2/layout/UserLayout';
import SecondarySidebar, { SecondaryMenuItem } from '../../../common/SecondarySidebar';
import { AdminUser } from '../../../../types/admin-v2';

interface CompanyDashboardProps {
  user: AdminUser;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

const CompanyDashboard: React.FC<CompanyDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { companyId } = useParams<{ companyId: string }>();

  // Create menu items for secondary sidebar
  const companyMenuItems: SecondaryMenuItem[] = [
    {
      id: 'users',
      label: 'Users',
      icon: '●',
      onClick: () => onNavigate(`/dashboard/companies/${companyId}/users`)
    },
    {
      id: 'products',
      label: 'Product List',
      icon: '●',
      onClick: () => onNavigate(`/dashboard/companies/${companyId}/products`)
    },
    {
      id: 'suppliers',
      label: 'Supplier List',
      icon: '●',
      onClick: () => onNavigate(`/dashboard/companies/${companyId}/suppliers`)
    },
    {
      id: 'user-rights',
      label: 'User Right Definition',
      icon: '●',
      onClick: () => onNavigate(`/dashboard/companies/${companyId}/user-rights`)
    }
  ];

  // Determine active item based on current route
  const getActiveItemId = () => {
    if (currentRoute.includes('/user-rights')) return 'user-rights';
    if (currentRoute.includes('/users')) return 'users';
    if (currentRoute.includes('/products')) return 'products';
    if (currentRoute.includes('/suppliers')) return 'suppliers';
    return undefined;
  };

  const isAdmin = user.role === 'admin';
  const LayoutComponent = isAdmin ? AdminLayout : UserLayout;

  return (
    <LayoutComponent
      user={user}
      currentRoute={currentRoute}
      onNavigate={onNavigate}
      onLogout={onLogout}
      pageTitle={`Company Management - ${companyId}`}
      pageDescription="Manage company settings and information"
      {...(isAdmin ? {
        secondarySidebar: (
          <SecondarySidebar
            title="Company Management"
            menuItems={companyMenuItems}
            activeItemId={getActiveItemId()}
          />
        )
      } : {
        secondarySidebar: {
          title: "Company Management",
          menuItems: companyMenuItems,
          activeItemId: getActiveItemId()
        }
      })}
    >
      <div style={{ padding: '24px' }}>
        <h1>Company Dashboard</h1>
        <p>Company ID: {companyId}</p>
        <p>Select an option from the sidebar to manage this company.</p>
      </div>
    </LayoutComponent>
  );
};

export default CompanyDashboard;
